import { Link, useNavigate } from 'react-router-dom';
import { isTerminalMatterStatus } from '@/lib/matterStatus';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, CircleNotch as Loader2 } from '@phosphor-icons/react';
import { RowActions } from '@/components/shared/RowActions';
import { fetchMyMatters } from '@/lib/solicitorPortalApi';

export default function MyMattersPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['portal', 'matters'],
    queryFn: fetchMyMatters,
  });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Your matters
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every matter where your firm is the instructing solicitor.
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading your matters…
        </div>
      )}

      {isError && (
        <div className="rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-5 text-sm text-red-800 dark:text-red-300">
          {error instanceof Error ? error.message : 'Could not load matters.'}
        </div>
      )}

      {data && data.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-card p-10 text-center">
          <Briefcase className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <div className="mb-1 text-sm font-medium text-foreground">No matters yet</div>
          <p className="text-xs text-muted-foreground">
            When you open a matter with this chambers, it will appear here.
          </p>
        </div>
      )}

      {data && data.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30">
              <tr className="text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Case</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Lead counsel</th>
                <th className="px-4 py-3">Court</th>
                <th className="px-4 py-3">Updated</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.map((m) => (
                // The whole row highlights on hover, so that is where a solicitor clicks.
                // Only the case-name cell was a link, so clicking anywhere else - status,
                // counsel, court, date - did nothing at all and the portal looked broken.
                <tr
                  key={m.id}
                  onClick={() => navigate(`/solicitor/matters/${m.id}`)}
                  className="cursor-pointer hover:bg-muted/30"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/solicitor/matters/${m.id}`}
                      className="block"
                    >
                      <div className="font-medium text-foreground hover:text-brand-700 dark:hover:text-brand-300">
                        {m.caseName}
                      </div>
                      <div className="text-xs text-muted-foreground">{m.caseNumber}</div>
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={m.status} />
                  </td>
                  <td className="px-4 py-3">
                    {m.leadBarrister ? (
                      <div className="text-foreground">
                        {m.leadBarrister.user.firstName} {m.leadBarrister.user.lastName}
                        {m.leadBarrister.silkYear && (
                          <span className="ml-1 text-[10px] font-semibold uppercase text-brand-600 dark:text-brand-400">
                            KC
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs italic text-muted-foreground">
                        Awaiting allocation
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-foreground">
                    {m.court?.shortName ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {new Date(m.updatedAt).toLocaleDateString('en-GB')}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <RowActions onView={() => navigate(`/solicitor/matters/${m.id}`)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const label = status.replace(/_/g, ' ');
  const tone =
    status === 'active' || status === 'intake'
      ? 'bg-brand-100 text-brand-700 dark:text-brand-300'
      : isTerminalMatterStatus(status)
      ? 'bg-muted text-muted-foreground'
      : 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300';
  return (
    <span className={`rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${tone}`}>
      {label}
    </span>
  );
}
